import React, { createContext, useContext, useState, useEffect } from 'react';
import { Language } from '../types';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations: Record<Language, Record<string, string>> = {
  en: {
    nav_home: "Home",
    nav_services: "Services",
    nav_websites: "Websites",
    nav_portfolio: "Portfolio",
    nav_faq: "FAQ",
    nav_pos: "SwiftPOS",
    nav_contact: "Contact",
    btn_get_started: "Get Started",
    hero_eyebrow: "Enterprise-Ready Technology",
    hero_title_1: "Build Your",
    hero_title_2: "Digital Presence",
    hero_title_3: "With Swiftech",
    hero_lead: "We design and build high-performance business websites, premium QR menu solutions, and custom e-commerce platforms that drive real-world results.",
    btn_launch_project: "Launch Your Project",
    btn_explore_work: "Explore Our Work",
    services_eyebrow: "Our Expertise",
    services_title: "Precision Engineering for Every Platform",
    services_desc: "From initial strategy to final deployment, we provide the digital tools your business needs to excel in a modern market.",
    templates_eyebrow: "Design Library",
    templates_title: "Premium Website Samples",
    portfolio_eyebrow: "Recent Work",
    portfolio_title: "Proven Results for Local & Global Brands",
    faq_eyebrow: "Assistance",
    faq_title: "Common Questions & Inquiries",
    contact_title: "Let's build something exceptional.",
    contact_desc: "Whether you need a new website or a digital menu, our team is ready to help your business grow.",
    label_email_us: "EMAIL US",
    label_call_us: "CALL US",
    label_name: "NAME",
    label_email: "EMAIL",
    label_service: "SERVICE REQUIRED",
    label_message: "MESSAGE",
    btn_send_message: "Send Message",
    footer_desc: "Architecting premium digital solutions for the modern era. Focused on speed, design, and conversion.",
    footer_col_services: "Services",
    footer_col_legal: "Legal",
    link_privacy: "Privacy Policy",
    link_terms: "Terms of Service",
    copyright: "© 2026 Swiftech. All rights reserved.",
    placeholder_service: "Select a service...",
    opt_business: "Business Website",
    opt_qr_menu: "QR Menu Website",
    opt_ecommerce: "E-Commerce Store",
    opt_custom: "Custom Web Solution",
    opt_redesign: "Website Redesign",
    // SwiftPOS translations
    pos_hero_badge: "Modern POS Platform",
    pos_hero_title_1: "Run Your",
    pos_hero_title_2: "Entire Business.",
    pos_hero_desc: "Powerful POS and business management software built for restaurants, cafés, retail stores, and growing businesses. One modern system to manage sales, inventory, orders, staff, and customers.",
    pos_btn_get: "Get SwiftPOS",
    pos_btn_features: "Explore Features",
    pos_trust_free: "Free to Start",
    pos_trust_offline: "Works Offline",
    pos_trust_support: "24/7 Support",
    // SwiftPOS simple product page
    pos_simple_title: "SwiftPOS",
    pos_simple_sub: "Modern POS for your business",
    pos_simple_desc: "Manage sales, products, inventory, customers, orders, and more from one simple POS system.",
    pos_simple_btn_get: "Get SwiftPOS Now",
    pos_simple_feat_title: "Everything you need to sell and manage",
    pos_why_title: "Why SwiftPOS",
    pos_why_simple_t: "Simple",
    pos_why_simple_d: "Easy to learn and use.",
    pos_why_fast_t: "Fast",
    pos_why_fast_d: "Designed for quick daily operations.",
    pos_why_powerful_t: "Powerful",
    pos_why_powerful_d: "More than a cashier — manage your business from one place.",
    pos_why_flex_t: "Flexible",
    pos_why_flex_d: "Built for retail, restaurants, cafés, and small businesses.",
    pos_prev_title: "See SwiftPOS in action",
    pos_prev_desc: "A clean checkout and a clear view of your business.",
    pos_final_title: "Ready to simplify your business?",
    pos_final_desc: "Start using SwiftPOS and manage your business from one modern system.",
    pos_final_contact: "Contact SwiftTech",
    // Business Types
    pos_biz_eyebrow: "Industries",
    pos_biz_title_1: "Built for the Way Your",
    pos_biz_title_2: "Business Works",
    pos_biz_desc: "SwiftPOS adapts to your business model. Whether you run a restaurant, café, retail store, or any small business, SwiftPOS has you covered.",
    pos_biz_restaurant: "Restaurant",
    pos_biz_restaurant_desc: "Full kitchen & table management",
    pos_biz_cafe: "Café",
    pos_biz_cafe_desc: "Quick service & table tracking",
    pos_biz_coffee: "Coffee Shop",
    pos_biz_coffee_desc: "Fast checkout & loyalty programs",
    pos_biz_fastfood: "Fast Food",
    pos_biz_fastfood_desc: "High-speed order processing",
    pos_biz_retail: "Retail Store",
    pos_biz_retail_desc: "Inventory & barcode management",
    pos_biz_grocery: "Grocery Store",
    pos_biz_grocery_desc: "Weight-based & bulk items",
    pos_biz_minimarket: "Mini Market",
    pos_biz_minimarket_desc: "Quick sales & stock tracking",
    pos_biz_smallbiz: "Small Business",
    pos_biz_smallbiz_desc: "Everything you need to start",
    // Features
    pos_features_eyebrow: "Features",
    pos_features_title_1: "Everything Your Business",
    pos_features_title_2: "Needs",
    pos_features_desc: "From sales to staff management, SwiftPOS gives you all the tools to run your business efficiently.",
    pos_feat_sales: "Sales & POS",
    pos_feat_sales_desc: "Lightning-fast checkout with multiple payment methods, discounts, and tax support.",
    pos_feat_inventory: "Inventory Management",
    pos_feat_inventory_desc: "Track stock levels, set alerts, manage categories, and handle product variations.",
    pos_feat_orders: "Order Management",
    pos_feat_orders_desc: "Manage dine-in, takeout, and delivery orders with real-time status tracking.",
    pos_feat_customers: "Customer Management",
    pos_feat_customers_desc: "Build customer profiles, track purchase history, and manage loyalty programs.",
    pos_feat_staff: "Employee Management",
    pos_feat_staff_desc: "Role-based access control, shift management, and activity tracking.",
    pos_feat_reports: "Reports & Analytics",
    pos_feat_reports_desc: "Sales reports, profit analysis, product performance, and business insights.",
    pos_feat_receipts: "Digital Receipts",
    pos_feat_receipts_desc: "PDF receipts, digital invoices, WhatsApp sharing, and printing support.",
    pos_feat_qrmenu: "QR Menu",
    pos_feat_qrmenu_desc: "Digital menus with QR codes for restaurants and cafés.",
    pos_feat_barcode: "Barcode Scanner",
    pos_feat_barcode_desc: "Scan products for faster checkout and inventory management.",
    pos_feat_printers: "Printer Management",
    pos_feat_printers_desc: "Support for receipt printers, kitchen printers, and label printers.",
    pos_feat_multistore: "Multi-Store Management",
    pos_feat_multistore_desc: "Manage multiple locations from a central dashboard.",
    pos_feat_offline: "Offline-First POS",
    pos_feat_offline_desc: "Keep selling even without internet. Data syncs when connection returns.",
    // POS Experience
    pos_exp_eyebrow: "POS Experience",
    pos_exp_title_1: "Fast, Simple,",
    pos_exp_title_2: "Powerful Selling",
    pos_exp_desc: "SwiftPOS is designed for speed. Every interaction is optimized for fast checkout and efficient business operations.",
    pos_exp_fast: "Lightning-fast product selection",
    pos_exp_barcode: "Barcode scanning for quick checkout",
    pos_exp_payments: "Multiple payment methods supported",
    pos_exp_receipts: "Digital and printed receipts",
    pos_exp_printers: "Automatic printer integration",
    pos_exp_offline: "Works without internet connection",
    // Inventory
    pos_inv_eyebrow: "Inventory",
    pos_inv_title_1: "Smart Inventory",
    pos_inv_title_2: "Management",
    pos_inv_desc: "Keep track of every product. Set stock alerts, manage categories, and never run out of important items.",
    pos_inv_products: "Product Catalog",
    pos_inv_levels: "Stock Levels",
    pos_inv_movements: "Stock Movements",
    pos_inv_barcode: "Barcode Management",
    // Analytics
    pos_analytics_eyebrow: "Analytics",
    pos_analytics_title_1: "Business Intelligence",
    pos_analytics_title_2: "Built In",
    pos_analytics_desc: "Real-time dashboards and reports to help you understand your business and make data-driven decisions.",
    pos_stat_sales: "Today's Sales",
    pos_stat_orders: "Orders",
    pos_stat_profit: "Profit",
    pos_stat_products: "Products",
    pos_stat_lowstock: "Low Stock",
    pos_stat_top: "Top Items",
    pos_analytics_sales: "Sales Trend",
    pos_analytics_orders: "Order Trend",
    pos_analytics_profit: "Profit Trend",
    // Staff
    pos_staff_eyebrow: "Staff Management",
    pos_staff_title_1: "Manage Your",
    pos_staff_title_2: "Team",
    pos_staff_desc: "Role-based access control ensures every team member has the right permissions. Track shifts, activity, and performance.",
    pos_role_owner: "Owner",
    pos_role_admin: "Admin",
    pos_role_manager: "Manager",
    pos_role_cashier: "Cashier",
    pos_role_waiter: "Waiter",
    pos_role_kitchen: "Kitchen Staff",
    pos_role_inventory: "Inventory Staff",
    pos_role_delivery: "Delivery",
    // Kitchen
    pos_kitchen_eyebrow: "Restaurant & Kitchen",
    pos_kitchen_title_1: "Streamlined Kitchen",
    pos_kitchen_title_2: "Operations",
    pos_kitchen_desc: "From order to completion, manage the entire kitchen workflow with real-time updates and printer integration.",
    pos_kitchen_order: "Place Order",
    pos_kitchen_order_desc: "Customer places order via POS or QR menu",
    pos_kitchen_kitchen: "Kitchen Receives",
    pos_kitchen_kitchen_desc: "Order appears on kitchen display instantly",
    pos_kitchen_prep: "Preparation",
    pos_kitchen_prep_desc: "Kitchen staff prepares the order",
    pos_kitchen_complete: "Complete",
    pos_kitchen_complete_desc: "Order ready for pickup or delivery",
    // Offline
    pos_offline_eyebrow: "Offline-First",
    pos_offline_title_1: "Keep Selling, Even When the",
    pos_offline_title_2: "Internet Doesn't",
    pos_offline_desc: "SwiftPOS is designed to work without internet. Continue processing sales, and data syncs automatically when connection returns.",
    pos_offline_online: "Online — Full functionality",
    pos_offline_offline: "Offline — POS continues working",
    pos_offline_continue: "Keep selling — All data saved locally",
    pos_offline_restored: "Connection restored — Data uploads",
    pos_offline_sync: "Automatic sync — Everything up to date",
    // Devices
    pos_devices_eyebrow: "Multi-Device",
    pos_devices_title_1: "Works on All",
    pos_devices_title_2: "Your Devices",
    pos_devices_desc: "SwiftPOS runs on Android, iOS, web browsers, tablets, and POS terminals. Use the devices you already have.",
    pos_device_android: "Android",
    pos_device_ios: "iOS",
    pos_device_web: "Web Browser",
    pos_device_tablet: "Tablet",
    pos_device_terminal: "POS Terminal",
    pos_device_printer: "Printer",
    // Multi-Store
    pos_multi_eyebrow: "Multi-Store",
    pos_multi_title_1: "Manage Multiple",
    pos_multi_title_2: "Locations",
    pos_multi_desc: "Growing business? Manage all your stores from one central dashboard. Monitor sales, inventory, and staff across locations.",
    pos_multi_locations: "Multiple store locations",
    pos_multi_central: "Central management dashboard",
    pos_multi_inventory: "Inventory visibility across stores",
    pos_multi_sales: "Sales monitoring per location",
    pos_multi_staff: "Staff management across branches",
    pos_multi_reports: "Store-level and combined reports",
    // Receipts
    pos_receipts_eyebrow: "Digital Receipts",
    pos_receipts_title_1: "Modern Receipt",
    pos_receipts_title_2: "Experience",
    pos_receipts_desc: "Generate PDF receipts, digital invoices, share via WhatsApp, or print. Keep records of every transaction.",
    pos_receipt_pdf: "PDF Receipts",
    pos_receipt_invoice: "Digital Invoices",
    pos_receipt_whatsapp: "WhatsApp Sharing",
    pos_receipt_print: "Print Support",
    // Why SwiftPOS
    pos_why_eyebrow: "Why SwiftPOS",
    pos_why_title_1: "Why Businesses",
    pos_why_title_2: "Choose SwiftPOS",
    pos_why_desc: "SwiftPOS is built for modern businesses that need speed, reliability, and simplicity.",
    pos_why_fast: "Fast",
    pos_why_fast_desc: "Lightning-fast checkout and smooth performance on any device.",
    pos_why_modern: "Modern",
    pos_why_modern_desc: "Clean, intuitive interface designed for the way you work today.",
    pos_why_simple: "Simple",
    pos_why_simple_desc: "Easy to learn, easy to use. Get started in minutes, not hours.",
    pos_why_reliable: "Reliable",
    pos_why_reliable_desc: "Built for 24/7 business operations with enterprise-grade stability.",
    pos_why_offline: "Offline-Ready",
    pos_why_offline_desc: "Keep selling without internet. Data syncs automatically when connected.",
    pos_why_scalable: "Scalable",
    pos_why_scalable_desc: "From one store to hundreds. SwiftPOS grows with your business.",
    // How It Works
    pos_how_eyebrow: "Getting Started",
    pos_how_title_1: "Up and Running in",
    pos_how_title_2: "4 Simple Steps",
    pos_how_desc: "Start selling in minutes. No complex setup required.",
    pos_how_step1: "Create Your Business",
    pos_how_step1_desc: "Set up your business profile and preferences",
    pos_how_step2: "Add Products",
    pos_how_step2_desc: "Import your products or add them manually",
    pos_how_step3: "Start Selling",
    pos_how_step3_desc: "Begin processing sales immediately",
    pos_how_step4: "Manage & Grow",
    pos_how_step4_desc: "Use reports and analytics to grow",
    // Final CTA
    pos_cta_title: "Ready to Run Your Business Smarter?",
    pos_cta_desc: "Bring your sales, inventory, orders, staff, customers, and analytics into one modern platform.",
    pos_btn_contact: "Contact SwiftTech",
    // SwiftPOS ad-landing rebuild
    pos_seo_title: "SwiftPOS — Modern POS for Restaurants, Cafés & Retail | Swiftech",
    pos_seo_desc: "SwiftPOS is a modern, offline-ready point of sale for restaurants, cafés and retail. Free to start. Download on Google Play.",
    pos_hero_play: "Get it on Google Play",
    pos_hero_free_note: "Free to start · No credit card",
    pos_stat_modules_l: "Business modules",
    pos_stat_offline_l: "Works offline",
    pos_stat_lang_l: "English + Arabic",
    pos_stat_all_l: "One system, everything",
    pos_steps_eyebrow: "Getting started",
    pos_steps_title: "Selling in 3 simple steps",
    pos_step1_t: "Download free",
    pos_step1_d: "Get SwiftPOS from Google Play and create your business in minutes.",
    pos_step2_t: "Add your products",
    pos_step2_d: "Add items, prices and categories — or import your existing list.",
    pos_step3_t: "Start selling",
    pos_step3_d: "Checkout in seconds, print receipts, and watch inventory update itself.",
    pos_price_eyebrow: "Pricing",
    pos_price_title: "Simple pricing that grows with you",
    pos_price_desc: "Start free. Upgrade when your business needs more.",
    pos_plan_free: "Starter",
    pos_plan_free_d: "Everything a small counter needs to start selling today.",
    pos_plan_pro: "Pro",
    pos_plan_pro_d: "For growing restaurants and stores that need the full system.",
    pos_price_free: "$0",
    pos_price_pro: "$9",
    pos_price_suffix: "/mo",
    pos_popular: "Most popular",
    pos_plan_cta_free: "Download free",
    pos_plan_cta_pro: "Get Pro",
    pos_plan_everything: "Everything in Starter, plus:",
    pos_plan_limit: "1 store · 1 device",
    pos_plan_support: "Priority support",
    pos_testi_eyebrow: "Loved locally",
    pos_testi_title: "Businesses running on SwiftPOS",
    pos_t1_q: "We replaced our old cash register in one afternoon. The staff learned it in minutes.",
    pos_t1_n: "Restaurant owner, Beirut",
    pos_t2_q: "It keeps selling even when our internet drops. That alone paid for itself.",
    pos_t2_n: "Café owner, Zahle",
    pos_t3_q: "Inventory finally matches reality. I check my store from my phone at home.",
    pos_t3_n: "Mini-market owner, Jounieh",
    pos_faq_eyebrow: "Questions",
    pos_faq_title: "Everything else you're wondering",
    pos_faq1_q: "Is SwiftPOS really free to start?",
    pos_faq1_a: "Yes. The Starter plan is free with no credit card. You only pay if you upgrade to Pro.",
    pos_faq2_q: "Does it work without internet?",
    pos_faq2_a: "Yes. SwiftPOS is offline-first: keep selling normally and everything syncs when you're back online.",
    pos_faq3_q: "Which devices does it run on?",
    pos_faq3_a: "The app runs on Android phones and tablets from Google Play, with a web dashboard for owners and managers.",
    pos_faq4_q: "Is it available in Arabic?",
    pos_faq4_a: "Fully. The whole system — app, receipts and dashboard — works in English and Arabic with RTL layout.",
    pos_faq5_q: "Can I manage more than one store?",
    pos_faq5_a: "Yes, multi-store management is part of the Pro plan, with per-branch sales and stock visibility.",
    pos_faq6_q: "Will you help me set it up?",
    pos_faq6_a: "Of course. Contact the SwiftTech team and we'll help you add products and go live.",
    sending: "Sending...",
    skip_to_content: "Skip to main content",
    hero_cta_desc: "Click to start your project with us",
    loading: "Loading..."
  },
  ar: {
    nav_home: "الرئيسية",
    nav_services: "خدماتنا",
    nav_websites: "المواقع",
    nav_portfolio: "أعمالنا",
    nav_faq: "الأسئلة الشائعة",
    nav_pos: "سويفت بوس",
    nav_contact: "اتصل بنا",
    btn_get_started: "ابدأ الآن",
    hero_eyebrow: "تكنولوجيا جاهزة للمؤسسات",
    hero_title_1: "ابنِ",
    hero_title_2: "وجودك الرقمي",
    hero_title_3: "مع سويفتك",
    hero_lead: "نصمم ونبني مواقع أعمال عالية الأداء، وحلول قوائم QR متميزة، ومنصات تجارة إلكترونية مخصصة تحقق نتائج واقعية.",
    btn_launch_project: "أطلق مشروعك",
    btn_explore_work: "استكشف أعمالنا",
    services_eyebrow: "خبرتنا",
    services_title: "هندسة دقيقة لكل منصة",
    services_desc: "من الاستراتيجية الأولية إلى النشر النهائي، نوفر الأدوات الرقمية التي يحتاجها عملك للتميز في السوق الحديث.",
    templates_eyebrow: "مكتبة التصميم",
    templates_title: "عينات مواقع متميزة",
    portfolio_eyebrow: "أعمالنا الأخيرة",
    portfolio_title: "نتائج مثبتة لعلامات تجارية محلية وعالمية",
    faq_eyebrow: "المساعدة",
    faq_title: "الأسئلة والاستفسارات الشائعة",
    contact_title: "لنبنِ شيئاً استثنائياً.",
    contact_desc: "سواء كنت بحاجة إلى موقع ويب جديد أو قائمة رقمية، فريقنا مستعد لمساعدة عملك على النمو.",
    label_email_us: "راسلنا",
    label_call_us: "اتصل بنا",
    label_name: "الاسم",
    label_email: "البريد الإلكتروني",
    label_service: "الخدمة المطلوبة",
    label_message: "الرسالة",
    btn_send_message: "إرسال الرسالة",
    footer_desc: "هندسة حلول رقمية متميزة للعصر الحديث. نركز على السرعة والتصميم والتحويل.",
    footer_col_services: "الخدمات",
    footer_col_legal: "قانوني",
    link_privacy: "سياسة الخصوصية",
    link_terms: "شروط الخدمة",
    copyright: "© 2026 سويفتك. جميع الحقوق محفوظة.",
    placeholder_service: "اختر خدمة...",
    opt_business: "موقع أعمال",
    opt_qr_menu: "موقع قائمة QR",
    opt_ecommerce: "متجر إلكتروني",
    opt_custom: "حل ويب مخصص",
    opt_redesign: "إعادة تصميم موقع",
    // SwiftPOS translations
    pos_hero_badge: "منصة نقاط بيع حديثة",
    pos_hero_title_1: "أدر",
    pos_hero_title_2: "عملك بالكامل.",
    pos_hero_desc: "برنامج نقاط بيع وإدارة أعمال قوي مصمم للمطاعم والمقاهي ومحلات التجزئة والأنشطة الناشئة. نظام واحد حديث لإدارة المبيعات والمخزون والطلبات والموظفين والعملاء.",
    pos_btn_get: "احصل على SwiftPOS",
    pos_btn_features: "استكشف المميزات",
    pos_trust_free: "مجاني للبدء",
    pos_trust_offline: "يعمل بدون إنترنت",
    pos_trust_support: "دعم على مدار الساعة",
    // SwiftPOS simple product page
    pos_simple_title: "SwiftPOS",
    pos_simple_sub: "نظام نقاط بيع عصري لعملك",
    pos_simple_desc: "أدر المبيعات والمنتجات والمخزون والعملاء والطلبات والمزيد من نظام نقاط بيع واحد بسيط.",
    pos_simple_btn_get: "احصل على SwiftPOS الآن",
    pos_simple_feat_title: "كل ما تحتاجه للبيع والإدارة",
    pos_why_title: "لماذا SwiftPOS",
    pos_why_simple_t: "بسيط",
    pos_why_simple_d: "سهل التعلم والاستخدام.",
    pos_why_fast_t: "سريع",
    pos_why_fast_d: "مصمم للعمليات اليومية السريعة.",
    pos_why_powerful_t: "قوي",
    pos_why_powerful_d: "أكثر من صندوق — أدر عملك من مكان واحد.",
    pos_why_flex_t: "مرن",
    pos_why_flex_d: "مصمم للمتاجر والمطاعم والمقاهي والشركات الصغيرة.",
    pos_prev_title: "شاهد SwiftPOS أثناء العمل",
    pos_prev_desc: "شاشة بيع نظيفة ونظرة واضحة على عملك.",
    pos_final_title: "جاهز لتبسيط عملك؟",
    pos_final_desc: "ابدأ باستخدام SwiftPOS وأدر عملك من نظام عصري واحد.",
    pos_final_contact: "تواصل مع SwiftTech",
    // Business Types
    pos_biz_eyebrow: "القطاعات",
    pos_biz_title_1: "مصمم لطريقة عمل",
    pos_biz_title_2: "نشاطك التجاري",
    pos_biz_desc: "يتكيّف SwiftPOS مع نموذج عملك. سواء كنت تدير مطعماً أو مقهى أو متجر تجزئة أو أي نشاط صغير، SwiftPOS يغطي احتياجاتك.",
    pos_biz_restaurant: "مطعم",
    pos_biz_restaurant_desc: "إدارة المطبخ والطاولات",
    pos_biz_cafe: "مقهى",
    pos_biz_cafe_desc: "خدمة سريعة وتتبع الطاولات",
    pos_biz_coffee: "مقهى قهوة",
    pos_biz_coffee_desc: "دفع سريع وبرامج الولاء",
    pos_biz_fastfood: "وجبات سريعة",
    pos_biz_fastfood_desc: "معالجة الطلبات بأقصى سرعة",
    pos_biz_retail: "متجر تجزئة",
    pos_biz_retail_desc: "إدارة المخزون والباركود",
    pos_biz_grocery: "سوبر ماركت",
    pos_biz_grocery_desc: "أصناف بالوزن وبالجملة",
    pos_biz_minimarket: "متجر صغير",
    pos_biz_minimarket_desc: "مبيعات سريعة وتتبع المخزون",
    pos_biz_smallbiz: "نشاط صغير",
    pos_biz_smallbiz_desc: "كل ما تحتاجه للبدء",
    // Features
    pos_features_eyebrow: "المميزات",
    pos_features_title_1: "كل ما يحتاجه عملك",
    pos_features_title_2: "",
    pos_features_desc: "من المبيعات إلى إدارة الموظفين، يمنحك SwiftPOS كل الأدوات لإدارة أعمالك بكفاءة.",
    pos_feat_sales: "المبيعات ونقطة البيع",
    pos_feat_sales_desc: "دفع فائق السرعة مع طرق دفع متuenta وخصومات وضرائب.",
    pos_feat_inventory: "إدارة المخزون",
    pos_feat_inventory_desc: "تتبع مستويات المخزون وتنبيهات الأصناف وإدارة الفئات.",
    pos_feat_orders: "إدارة الطلبات",
    pos_feat_orders_desc: "إدارة طلبات الصالة والوجبات السريعة والتوصيل.",
    pos_feat_customers: "إدارة العملاء",
    pos_feat_customers_desc: "بناء ملفات العملاء وتتبع سجل الشراء وبرامج الولاء.",
    pos_feat_staff: "إدارة الموظفين",
    pos_feat_staff_desc: "التحكم في الأدوار وإدارة المناوبات وتتبع النشاط.",
    pos_feat_reports: "التقارير والتحليلات",
    pos_feat_reports_desc: "تقارير المبيعات وتحليل الأرباح وأداء المنتجات.",
    pos_feat_receipts: "الإيصالات الرقمية",
    pos_feat_receipts_desc: "إيصالات PDF وفواتير رقمية ومشاركة عبر واتساب.",
    pos_feat_qrmenu: "قائمة QR",
    pos_feat_qrmenu_desc: "قوائم رقمية بأكواد QR للمطاعم والمقاهي.",
    pos_feat_barcode: "ماسح الباركود",
    pos_feat_barcode_desc: "مسح منتجات أسرع لإتمام المعاملات.",
    pos_feat_printers: "إدارة الطابعات",
    pos_feat_printers_desc: "دعم طابعات الإيصالات والمطبخ والملصقات.",
    pos_feat_multistore: "إدارة فروع متعددة",
    pos_feat_multistore_desc: "إدارة مواقع متعددة من لوحة تحكم مركزية.",
    pos_feat_offline: "نقطة بيع بدون إنترنت",
    pos_feat_offline_desc: "تابع البيع حتى بدون إنترنت. البيانات تتزامن عند عودة الاتصال.",
    // POS Experience
    pos_exp_eyebrow: "تجربة نقطة البيع",
    pos_exp_title_1: "بيع سريع وبسيط",
    pos_exp_title_2: "وقوي",
    pos_exp_desc: "مصمم للسرعة. كل تفاعل محسّن لدفع سريع وعمليات تجارية فعالة.",
    pos_exp_fast: "اختيار المنتجات بسرعة فائقة",
    pos_exp_barcode: "مسح الباركود للدفع السريع",
    pos_exp_payments: "طرق دفع متعددة مدعومة",
    pos_exp_receipts: "إيصالات رقمية ومطبوعة",
    pos_exp_printers: "تكامل تلقائي مع الطابعات",
    pos_exp_offline: "يعمل بدون اتصال بالإنترنت",
    // Inventory
    pos_inv_eyebrow: "المخزون",
    pos_inv_title_1: "إدارة مخزون",
    pos_inv_title_2: "ذكية",
    pos_inv_desc: "تتبع كل منتج. اضبط تنبيهات المخزون وأدر الفئات ولا تتأخر في أي صنف مهم.",
    pos_inv_products: "كتالوج المنتجات",
    pos_inv_levels: "مستويات المخزون",
    pos_inv_movements: "حركات المخزون",
    pos_inv_barcode: "إدارة الباركود",
    // Analytics
    pos_analytics_eyebrow: "التحليلات",
    pos_analytics_title_1: "ذكاء أعمال",
    pos_analytics_title_2: "مدمج",
    pos_analytics_desc: "لوحات معلومات وتقارير لحظية لمساعدتك على فهم عملك واتخاذ قرارات مبنية على البيانات.",
    pos_stat_sales: "مبيعات اليوم",
    pos_stat_orders: "الطلبات",
    pos_stat_profit: "الأرباح",
    pos_stat_products: "المنتجات",
    pos_stat_lowstock: "مخزون منخفض",
    pos_stat_top: "الأصناف الأولى",
    pos_analytics_sales: "اتجاه المبيعات",
    pos_analytics_orders: "اتجاه الطلبات",
    pos_analytics_profit: "اتجاه الأرباح",
    // Staff
    pos_staff_eyebrow: "إدارة الموظفين",
    pos_staff_title_1: "أدر",
    pos_staff_title_2: "فريقك",
    pos_staff_desc: "التحكم في الأدوار يضمن لكل عضو في الفريق الصلاحيات المناسبة. تتبع المناوبات والنشاط والأداء.",
    pos_role_owner: "المالك",
    pos_role_admin: "المدير",
    pos_role_manager: "مدير",
    pos_role_cashier: "أمين الصندوق",
    pos_role_waiter: "ناقل الطلبات",
    pos_role_kitchen: "طاقم المطبخ",
    pos_role_inventory: "طاقم المخزون",
    pos_role_delivery: "التوصيل",
    // Kitchen
    pos_kitchen_eyebrow: "المطاعم والمطبخ",
    pos_kitchen_title_1: "عمليات مطبخ",
    pos_kitchen_title_2: "مُنسّقة",
    pos_kitchen_desc: "من الطلب إلى الإنجاز، أدر سير عمل المطبخ بالكامل مع تحديثات فورية وتكامل الطابعات.",
    pos_kitchen_order: "تقديم الطلب",
    pos_kitchen_order_desc: "العميل يقدم الطلب عبر نقطة البيع أو قائمة QR",
    pos_kitchen_kitchen: "المطبخ يستقبل",
    pos_kitchen_kitchen_desc: "يظهر الطلب على شاشة المطبخ فوراً",
    pos_kitchen_prep: "التحضير",
    pos_kitchen_prep_desc: "طاقم المطبخ يحضّر الطلب",
    pos_kitchen_complete: "الإنجاز",
    pos_kitchen_complete_desc: "الطلب جاهز للاستلام أو التوصيل",
    // Offline
    pos_offline_eyebrow: "بدون إنترنت",
    pos_offline_title_1: "تابع البيع حتى مع انقطاع",
    pos_offline_title_2: "الإنترنت",
    pos_offline_desc: "مصمم للعمل بدون إنترنت. واصل معالجة المبيعات، والبيانات تتزامن تلقائياً عند عودة الاتصال.",
    pos_offline_online: "متصل — وظائف كاملة",
    pos_offline_offline: "غير متصل — نقطة البيع تستمر",
    pos_offline_continue: "تابع البيع — البيانات محفوظة محلياً",
    pos_offline_restored: "عاد الاتصال — البيانات تُرفع",
    pos_offline_sync: "تزامن تلقائي — كل شيء محدث",
    // Devices
    pos_devices_eyebrow: "أجهزة متعددة",
    pos_devices_title_1: "يعمل على جميع",
    pos_devices_title_2: "أجهزتك",
    pos_devices_desc: "يعمل على Android وiOS والمتصفح والأجهزة اللوحية وأجهزة نقطة البيع. استخدم الأجهزة التي تملكها بالفعل.",
    pos_device_android: "أندرويد",
    pos_device_ios: "آيفون",
    pos_device_web: "متصفح الويب",
    pos_device_tablet: "جهاز لوحي",
    pos_device_terminal: "جهاز نقطة البيع",
    pos_device_printer: "طابعة",
    // Multi-Store
    pos_multi_eyebrow: "فروع متعددة",
    pos_multi_title_1: "أدر مواقع",
    pos_multi_title_2: "متعددة",
    pos_multi_desc: "نشاط متنمو؟ أدر جميع متاجرك من لوحة تحكم مركزية. راقب المبيعات والمخزون والموظفين عبر الفروع.",
    pos_multi_locations: "مواقع متاجر متعددة",
    pos_multi_central: "لوحة تحكم مركزية",
    pos_multi_inventory: "رؤية المخزون عبر الفروع",
    pos_multi_sales: "مراقبة المبيعات لكل موقع",
    pos_multi_staff: "إدارة الموظفين عبر الفروع",
    pos_multi_reports: "تقارير لكل فرع والمجمعة",
    // Receipts
    pos_receipts_eyebrow: "إيصالات رقمية",
    pos_receipts_title_1: "تجربة إيصالات",
    pos_receipts_title_2: "حديثة",
    pos_receipts_desc: "أنشئ إيصالات PDF وفواتير رقمية وشارك عبر واتساب أو اطبع. احفظ سجل كل معاملة.",
    pos_receipt_pdf: "إيصالات PDF",
    pos_receipt_invoice: "فواتير رقمية",
    pos_receipt_whatsapp: "مشاركة عبر واتساب",
    pos_receipt_print: "دعم الطباعة",
    // Why SwiftPOS
    pos_why_eyebrow: "لماذا SwiftPOS",
    pos_why_title_1: "لماذا تختار",
    pos_why_title_2: "SwiftPOS",
    pos_why_desc: "مصمم للأنشطة الحديثة التي تحتاج سرعة وبساطة وموثوقية.",
    pos_why_fast: "سريع",
    pos_why_fast_desc: "دفع فائق السرعة وأداء سلس على أي جهاز.",
    pos_why_modern: "حديث",
    pos_why_modern_desc: "واجهة نظيفة وبديهية مصممة لطريقة عملك اليوم.",
    pos_why_simple: "بسيط",
    pos_why_simple_desc: "سهل التعلم وسهل الاستخدام. ابدأ في دقائق.",
    pos_why_reliable: "موثوق",
    pos_why_reliable_desc: "مصمم لعمليات 24/7 مع استقرار على مستوى المؤسسات.",
    pos_why_offline: "جاهز للعمل بدون إنترنت",
    pos_why_offline_desc: "تابع البيع بدون إنترنت. البيانات تتزامن تلقائياً.",
    pos_why_scalable: "قابل للتوسع",
    pos_why_scalable_desc: "من متجر واحد إلى المئات. SwiftPOS ينمو مع عملك.",
    // How It Works
    pos_how_eyebrow: "البدء",
    pos_how_title_1: "جاهز في",
    pos_how_title_2: "4 خطوات بسيطة",
    pos_how_desc: "ابدأ البيع في دقائق. لا حاجة لإعداد معقد.",
    pos_how_step1: "أنشئ نشاطك التجاري",
    pos_how_step1_desc: "أعد ملف نشاطك التجاري والتفضيلات",
    pos_how_step2: "أضف المنتجات",
    pos_how_step2_desc: "استيراد منتجاتك أو إضافتها يدوياً",
    pos_how_step3: "ابدأ البيع",
    pos_how_step3_desc: "ابدأ معالجة المبيعات فوراً",
    pos_how_step4: "أدر ونمو",
    pos_how_step4_desc: "استخدم التقارير والتحليلات للنمو",
    // Final CTA
    pos_cta_title: "مستعد لإدارة عملك بذكاء؟",
    pos_cta_desc: "اجمع مبيعاتك ومخزونك وطلباتك وموظفيك وعملاءك وتحليلاتك في منصة حديثة واحدة.",
    pos_btn_contact: "تواصل مع SwiftTech",
    // SwiftPOS ad-landing rebuild
    pos_seo_title: "SwiftPOS — نظام نقاط بيع عصري للمطاعم والمقاهي والمتاجر | سويفتك",
    pos_seo_desc: "SwiftPOS نظام نقاط بيع عصري يعمل بدون إنترنت للمطاعم والمقاهي والمتاجر. مجاني للبدء. حمّله من Google Play.",
    pos_hero_play: "حمّله من Google Play",
    pos_hero_free_note: "مجاني للبدء · بدون بطاقة ائتمان",
    pos_stat_modules_l: "وحدات للأعمال",
    pos_stat_offline_l: "يعمل بدون إنترنت",
    pos_stat_lang_l: "عربي + إنجليزي",
    pos_stat_all_l: "نظام واحد لكل شيء",
    pos_steps_eyebrow: "البدء",
    pos_steps_title: "ابدأ البيع في 3 خطوات",
    pos_step1_t: "حمّل مجاناً",
    pos_step1_d: "حمّل SwiftPOS من Google Play وأنشئ نشاطك في دقائق.",
    pos_step2_t: "أضف منتجاتك",
    pos_step2_d: "أضف الأصناف والأسعار والفئات — أو استورد لائحتك الحالية.",
    pos_step3_t: "ابدأ البيع",
    pos_step3_d: "ادفع في ثوانٍ، اطبع الإيصالات، وشاهد المخزون يتحدث تلقائياً.",
    pos_price_eyebrow: "الأسعار",
    pos_price_title: "أسعار بسيطة تنمو معك",
    pos_price_desc: "ابدأ مجاناً. ارتقِ عندما يحتاج عملك المزيد.",
    pos_plan_free: "البداية",
    pos_plan_free_d: "كل ما يحتاجه المحل الصغير لبدء البيع اليوم.",
    pos_plan_pro: "الاحترافية",
    pos_plan_pro_d: "للمطاعم والمتاجر النامية التي تحتاج النظام الكامل.",
    pos_price_free: "$0",
    pos_price_pro: "$9",
    pos_price_suffix: "/شهر",
    pos_popular: "الأكثر طلباً",
    pos_plan_cta_free: "حمّل مجاناً",
    pos_plan_cta_pro: "احصل على Pro",
    pos_plan_everything: "كل مزايا البداية، بالإضافة:",
    pos_plan_limit: "متجر واحد · جهاز واحد",
    pos_plan_support: "دعم ذو أولوية",
    pos_testi_eyebrow: "محبوب محلياً",
    pos_testi_title: "أنشطة تعمل على SwiftPOS",
    pos_t1_q: "استبدلنا صندوقنا القديم في بعد ظهر واحد. تعلمه الموظفون في دقائق.",
    pos_t1_n: "صاحب مطعم، بيروت",
    pos_t2_q: "يتابع البيع حتى عند انقطاع الإنترنت. هذا وحده يستحق.",
    pos_t2_n: "صاحب مقهى، زحلة",
    pos_t3_q: "أخيراً المخزون يطابق الواقع. أراقب متجري من هاتفي في البيت.",
    pos_t3_n: "صاحب متجر صغير، جونيه",
    pos_faq_eyebrow: "أسئلة",
    pos_faq_title: "كل ما تتساءل عنه",
    pos_faq1_q: "هل SwiftPOS مجاني فعلاً للبدء؟",
    pos_faq1_a: "نعم. خطة البداية مجانية وبدون بطاقة ائتمان. تدفع فقط إذا ارتقيت إلى Pro.",
    pos_faq2_q: "هل يعمل بدون إنترنت؟",
    pos_faq2_a: "نعم. يعمل بدون إنترنت أولاً: تابع البيع طبيعياً وكل شيء يتزامن عند عودة الاتصال.",
    pos_faq3_q: "على أي أجهزة يعمل؟",
    pos_faq3_a: "التطبيق يعمل على هواتف وأجهزة أندرويد اللوحية من Google Play، مع لوحة ويب للمالكين والمدراء.",
    pos_faq4_q: "هل يتوفر بالعربية؟",
    pos_faq4_a: "بالكامل. النظام كله — التطبيق والإيصالات واللوحة — يعمل بالإنجليزية والعربية مع اتجاه RTL.",
    pos_faq5_q: "هل يمكنني إدارة أكثر من متجر؟",
    pos_faq5_a: "نعم، إدارة الفروع المتعددة جزء من خطة Pro، مع رؤية المبيعات والمخزون لكل فرع.",
    pos_faq6_q: "هل تساعدونني في الإعداد؟",
    pos_faq6_a: "بالتأكيد. تواصل مع فريق SwiftTech وسنساعدك على إضافة المنتجات والانطلاق.",
    sending: "جاري الإرسال...",
    skip_to_content: "تخطى إلى المحتوى الرئيسي",
    hero_cta_desc: "انقر لبدء مشروعك معنا",
    loading: "جاري التحميل..."
  }
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(
    (localStorage.getItem('sw_lang') as Language) || 'en'
  );

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('sw_lang', lang);
  };

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const t = (key: string) => translations[language][key] || key;

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};
